import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import TipLinkExtension from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import UploadButton from "../components/UploadButton";
import Seo from "../components/Seo";

const CATEGORIES = ["Aftercare","Inspiration","Feedback","Artist Spotlight","Community News","Style Deep-Dives","First Timer Guides"];

function BlogEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      TipLinkExtension.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Tell the story..." }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });
  if (!editor) return null;
  const addLink = () => {
    const url = window.prompt("URL");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };
  return (
    <div className="border-[0.5px] border-black/20 rounded-[8px] p-3" data-testid="tiptap-editor">
      <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b-[0.5px] border-black/10">
        <button type="button" className={`badge ${editor.isActive("bold")?"badge-flash":"badge-neutral"}`} onClick={()=>editor.chain().focus().toggleBold().run()} data-testid="tiptap-bold"><b>B</b></button>
        <button type="button" className={`badge ${editor.isActive("italic")?"badge-flash":"badge-neutral"}`} onClick={()=>editor.chain().focus().toggleItalic().run()} data-testid="tiptap-italic"><i>I</i></button>
        <button type="button" className={`badge ${editor.isActive("heading",{level:2})?"badge-flash":"badge-neutral"}`} onClick={()=>editor.chain().focus().toggleHeading({level: 2}).run()} data-testid="tiptap-h2">H2</button>
        <button type="button" className={`badge ${editor.isActive("heading",{level:3})?"badge-flash":"badge-neutral"}`} onClick={()=>editor.chain().focus().toggleHeading({level: 3}).run()} data-testid="tiptap-h3">H3</button>
        <button type="button" className={`badge ${editor.isActive("bulletList")?"badge-flash":"badge-neutral"}`} onClick={()=>editor.chain().focus().toggleBulletList().run()} data-testid="tiptap-ul">•</button>
        <button type="button" className={`badge ${editor.isActive("blockquote")?"badge-flash":"badge-neutral"}`} onClick={()=>editor.chain().focus().toggleBlockquote().run()} data-testid="tiptap-quote">"</button>
        <button type="button" className="badge badge-neutral" onClick={addLink} data-testid="tiptap-link">link</button>
        <UploadButton onUploaded={(url)=>editor.chain().focus().setImage({ src: url }).run()} label="image" testId="tiptap-image"/>
      </div>
      <EditorContent editor={editor}/>
    </div>
  );
}

export default function BlogEditorPage({ editMode = false }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ title: "", body: "", cover_image_url: "", category: "Inspiration", tags: [], status: "draft" });
  const [postId, setPostId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editMode && slug) {
      api.get(`/blog/posts/${slug}`).then((r) => {
        setForm({ title: r.data.title, body: r.data.body, cover_image_url: r.data.cover_image_url || "", category: r.data.category, tags: r.data.tags || [], status: r.data.status });
        setPostId(r.data.id);
      });
    }
  }, [editMode, slug]);

  if (!user) return <div className="container-x py-20">Sign in to write.</div>;

  const submit = async (status) => {
    setLoading(true);
    try {
      const payload = { ...form, status, tags: typeof form.tags === "string" ? form.tags.split(",").map(s=>s.trim()).filter(Boolean) : form.tags };
      if (editMode && postId) {
        const r = await api.patch(`/blog/posts/${postId}`, payload);
        toast.success(status === "published" ? "Published" : "Saved");
        nav(`/blog/${r.data.slug}`);
      } else {
        const r = await api.post("/blog/posts", payload);
        toast.success(status === "published" ? "Published" : "Saved as draft");
        nav(status === "published" ? `/blog/${r.data.slug}` : "/dashboard/blog");
      }
    } catch (e) { toast.error("Could not save"); } finally { setLoading(false); }
  };

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="blog-editor-page">
      <Seo title="Write a post" noindex/>
      <h1 className="font-serif text-4xl">{editMode ? "Edit post" : "Write a new post"}</h1>
      <div className="space-y-4 mt-8">
        <div>
          <label className="text-sm font-medium block mb-1.5">Title</label>
          <input className="input-field" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} placeholder="A great title" data-testid="blog-title-input"/>
        </div>
        <div>
          <label className="text-sm font-medium block mb-1.5">Cover image</label>
          <div className="flex gap-2">
            <input className="input-field" value={form.cover_image_url} onChange={(e)=>setForm({...form, cover_image_url: e.target.value})} placeholder="https://..."/>
            <UploadButton onUploaded={(url)=>setForm({...form, cover_image_url: url})} label="Upload" testId="blog-cover-upload"/>
          </div>
          {form.cover_image_url && <img src={form.cover_image_url} alt="" className="mt-3 rounded-[8px] max-h-64 object-cover"/>}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">Category</label>
            <select className="input-field" value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})} data-testid="blog-category">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">Tags (comma-separated)</label>
            <input className="input-field" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e)=>setForm({...form, tags: e.target.value})} placeholder="ink, healing"/>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium block mb-1.5">Body</label>
          <BlogEditor value={form.body} onChange={(html)=>setForm({...form, body: html})}/>
        </div>
        <div className="flex gap-3 pt-2">
          <button onClick={()=>submit("draft")} className="btn-outline" disabled={loading} data-testid="blog-save-draft">Save draft</button>
          <button onClick={()=>submit("published")} className="btn-filled" disabled={loading || !form.title || form.body?.trim() === ""} data-testid="blog-publish">Publish</button>
        </div>
      </div>
    </div>
  );
}
